import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Kanban,
  BookOpen,
  ClipboardCheck,
  Settings,
  Sliders,
  Users,
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  Compass,
  Sparkles,
  Briefcase,
} from "lucide-react";
import { UserProfile } from "../../types";
import { signOut } from "../../services/auth";
import { useNewVersionCheck } from "../../hooks/useNewVersionCheck";
import { FloatingAssistantDrawer } from "../assistant/FloatingAssistantDrawer";
import { HeaderRfpSwitcher } from "./HeaderRfpSwitcher";

interface AppLayoutProps {
  children: React.ReactNode;
  user: UserProfile | null;
  reviewBadgeCount?: number;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children, user, reviewBadgeCount }) => {
  const location = useLocation();
  const { isNewVersionAvailable, reload } = useNewVersionCheck();
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  // Dark mode with persistent local storage
  const [isDark, setIsDark] = useState<boolean>(() => {
    const saved = localStorage.getItem("mlc_theme");
    if (saved) return saved === "dark";
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("mlc_theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("mlc_theme", "light");
    }
  }, [isDark]);

  const navItems = [
    { name: "Pipeline", path: "/", icon: Kanban },
    { name: "Workspace", path: "/workspace", icon: Briefcase },
    { name: "Answer Library", path: "/library", icon: BookOpen },
    { name: "Review Queue", path: "/reviews", icon: ClipboardCheck },
    { name: "Settings", path: "/settings", icon: Settings },
    { name: "Platform Guide", path: "/guide", icon: Compass },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 transition-colors duration-200 dark:bg-slate-950 dark:text-slate-100">
      {/* Left Sidebar Navigation */}
      <aside className="glass fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-slate-200/80 bg-white/80 dark:border-slate-800/80 dark:bg-slate-900/80">
        {/* App Logo & Header */}
        <div className="flex h-16 items-center gap-3 border-b border-slate-200/60 px-5 dark:border-slate-800/60">
          <img
            src="/mlc-logo.png"
            alt="Multilingual Connections Logo"
            className="h-8 w-8 object-contain"
            onError={(e) => {
              // Fallback to favicon if logo missing
              (e.currentTarget as HTMLImageElement).src = "/favicon.png";
            }}
          />
          <div>
            <h1 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">
              MLC Proposal HQ
            </h1>
            <p className="text-[11px] text-[#2ba5b5] font-medium">RFP Response Workspace</p>
          </div>
        </div>

        {/* Primary Navigation Links */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navItems.map((item) => {
            const Icon = item.icon;

            if (item.name === "Settings") {
              const isSettingsRoot = location.pathname === "/settings";
              const isStaffActive = location.pathname.startsWith("/settings/staff");
              const isAnySettingsActive = location.pathname.startsWith("/settings");

              return (
                <div key={item.path} className="space-y-1">
                  <Link
                    to={item.path}
                    className={`group flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                      isAnySettingsActive
                        ? "bg-[#2ba5b5] text-white shadow-xs"
                        : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon
                        className={`h-4 w-4 transition-colors ${
                          isAnySettingsActive
                            ? "text-white"
                            : "text-slate-400 group-hover:text-slate-700 dark:text-slate-500 dark:group-hover:text-slate-300"
                        }`}
                      />
                      <span>{item.name}</span>
                    </div>
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform ${
                        isAnySettingsActive ? "text-white rotate-0" : "text-slate-400 -rotate-90"
                      }`}
                    />
                  </Link>

                  {/* Sub-navigation under Settings */}
                  <div className="ml-3 space-y-0.5 border-l-2 border-slate-200 pl-2 pt-0.5 dark:border-slate-800">
                    <Link
                      to="/settings"
                      className={`group flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all ${
                        isSettingsRoot
                          ? "bg-teal-50 text-[#2ba5b5] font-bold dark:bg-teal-950/60 dark:text-teal-300"
                          : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200"
                      }`}
                    >
                      <Sliders
                        className={`h-3.5 w-3.5 ${
                          isSettingsRoot ? "text-[#2ba5b5]" : "text-slate-400"
                        }`}
                      />
                      <span>General & AI</span>
                    </Link>

                    <Link
                      to="/settings/staff"
                      className={`group flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all ${
                        isStaffActive
                          ? "bg-teal-50 text-[#2ba5b5] font-bold dark:bg-teal-950/60 dark:text-teal-300"
                          : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200"
                      }`}
                    >
                      <Users
                        className={`h-3.5 w-3.5 ${isStaffActive ? "text-[#2ba5b5]" : "text-slate-400"}`}
                      />
                      <span>Staff Access & Roles</span>
                    </Link>
                  </div>
                </div>
              );
            }

            const isActive =
              item.path === "/"
                ? location.pathname === "/"
                : item.path === "/workspace"
                  ? location.pathname === "/workspace" ||
                    location.pathname.startsWith("/opportunities/")
                  : location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[#2ba5b5] text-white shadow-xs"
                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-200"
                }`}
              >
                <Icon
                  className={`h-4 w-4 transition-colors ${
                    isActive
                      ? "text-white"
                      : "text-slate-400 group-hover:text-slate-700 dark:text-slate-500 dark:group-hover:text-slate-300"
                  }`}
                />
                <span>{item.name}</span>
                {item.name === "Review Queue" && !!reviewBadgeCount && reviewBadgeCount > 0 && (
                  <span
                    aria-label={`${reviewBadgeCount} pending review items`}
                    className={`ml-auto inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold ${
                      isActive ? "bg-white text-[#2ba5b5]" : "bg-rose-500 text-white"
                    }`}
                  >
                    {reviewBadgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Profile & Footer Actions */}
        <div className="border-t border-slate-200/60 p-3 dark:border-slate-800/60">
          {user ? (
            <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-100/70 p-2.5 dark:bg-slate-800/60">
              <div className="flex items-center gap-2.5 overflow-hidden">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2ba5b5] text-xs font-bold text-white">
                    {user.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-slate-800 dark:text-slate-200">
                    {user.displayName}
                  </p>
                  <p className="truncate text-[10px] text-slate-500 dark:text-slate-400">
                    {user.email}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSignOut}
                title="Sign out"
                className="btn-animate rounded p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="text-center">
              <Link
                to="/login"
                className="block w-full rounded-lg bg-[#2ba5b5] py-2 text-xs font-medium text-white shadow-xs hover:bg-[#238997]"
              >
                Sign In
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex min-w-0 flex-1 flex-col pl-64">
        {/* Top Header Bar */}
        <header className="glass sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200/80 bg-white/70 px-8 dark:border-slate-800/80 dark:bg-slate-900/70">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-medium text-[#2ba5b5] dark:bg-teal-950/50 dark:text-teal-400 border border-teal-200/40 dark:border-teal-800/40">
              in-house-dev-mlc
            </span>
            <HeaderRfpSwitcher />
          </div>

          <div className="flex items-center gap-3">
            {/* Ask HQ Quick Assistant Button */}
            <button
              type="button"
              onClick={() => setIsAssistantOpen(true)}
              title="Ask Proposal HQ Assistant"
              className="btn-animate flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-xs hover:border-teal-300 hover:bg-teal-50 hover:text-[#2ba5b5] dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-teal-700 dark:hover:bg-slate-700"
            >
              <Sparkles className="h-3.5 w-3.5 text-[#2ba5b5]" />
              <span className="hidden sm:inline">Ask HQ</span>
            </button>

            {/* Dark / Light Mode Toggle */}
            <button
              type="button"
              onClick={() => setIsDark((prev) => !prev)}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              aria-label="Toggle theme"
              className="btn-animate flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-xs hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              {isDark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </header>

        {/* New Version Alert Banner */}
        {isNewVersionAvailable && (
          <div className="flex items-center justify-between border-b border-amber-300 bg-amber-50 px-8 py-2.5 text-xs font-medium text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
            <span>A new version of MLC Proposal HQ is available.</span>
            <button
              type="button"
              onClick={reload}
              className="btn-animate rounded-md bg-amber-600 px-3 py-1 font-semibold text-white shadow-2xs hover:bg-amber-700"
            >
              Reload
            </button>
          </div>
        )}

        {/* Page View Container */}
        <main className="flex-1 p-8">{children}</main>
      </div>

      {/* Global Floating Assistant Drawer */}
      <FloatingAssistantDrawer
        isOpen={isAssistantOpen}
        onToggle={() => setIsAssistantOpen((prev) => !prev)}
      />
    </div>
  );
};
